import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import useApi from '../api';
import RowDelete from '../ui/RowDelete';
import OrganizationsTab from './OrganizationsTab';
import UsersTab from './UsersTab';
import AdminAnalyticsTab from './AdminAnalyticsTab';
import ConversationsTab from './ConversationsTab';
import VisitorsTab from './VisitorsTab';
import LeadsTab from './LeadsTab';
import AgentRunsTab from './AgentRunsTab';
import MeteringTab from './MeteringTab';
import PlatformSqlTab from './PlatformSqlTab';

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { Calendar as CalendarIcon } from "lucide-react";
import { format } from "date-fns";

interface User {
    id: string;
    email: string;
    name: string;
    role?: string;
    organization_id?: string;
    organization_name?: string;
}

interface Organization {
    id: string;
    name: string;
    users?: User[];
    license_type: string;
    trial_end_date: string | null;
    settings: any;
    operator: boolean;
}

const DEFAULT_MODEL_SELECT_VALUE = "__default__";
const REASONING_EFFORT_LABELS: Record<string, string> = {
    none: 'None',
    low: 'Low',
    medium: 'Medium',
    high: 'High',
    xhigh: 'Extra high',
    max: 'Max',
};

// One entry per deployment (conversations.channel). Gateway and email fall
// back to the website override server-side when left on default.
const AGENT_MODEL_DEPLOYMENTS = [
    { key: 'website', label: 'Website agent model' },
    { key: 'app', label: 'App agent model' },
    { key: 'agent_gateway', label: 'Agent gateway model' },
    { key: 'email', label: 'Email agent model' },
] as const;

const Admin = () => {
    const { get, post, put, remove } = useApi();
    const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
    const [isUsersModalOpen, setIsUsersModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingOrg, setEditingOrg] = useState<Organization | null>(null);
    const [isUserEditModalOpen, setIsUserEditModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [searchParams, setSearchParams] = useSearchParams();
    const ADMIN_TABS = ["organizations", "users", "conversations", "visitors", "leads", "agent-runs", "analytics", "metering", "platform-sql"] as const;
    const tabFromUrl = searchParams.get("tab");
    const activeTab = (ADMIN_TABS as readonly string[]).includes(tabFromUrl || "")
        ? (tabFromUrl as string)
        : "organizations";
    const setActiveTab = (next: string) => {
        const params = new URLSearchParams(searchParams);
        if (next && next !== "organizations") {
            params.set("tab", next);
        } else {
            params.delete("tab");
        }
        setSearchParams(params, { replace: false });
    };
    const [refreshUsers, setRefreshUsers] = useState(0);
    const [rb2bConfigLoading, setRb2bConfigLoading] = useState(false);
    const [rb2bDomainsUpdating, setRb2bDomainsUpdating] = useState(false);
    const [rb2bEnabled, setRb2bEnabled] = useState(false);
    const [rb2bIdentifyAll, setRb2bIdentifyAll] = useState(false);
    const [rb2bDomains, setRb2bDomains] = useState<string[]>([]);
    const [rb2bDomainInput, setRb2bDomainInput] = useState('');
    const [rb2bResolutionLimit, setRb2bResolutionLimit] = useState<string>('');
    const [agentModelConfigLoading, setAgentModelConfigLoading] = useState(false);
    const [availableAgentModels, setAvailableAgentModels] = useState<string[]>([]);
    const [agentModels, setAgentModels] = useState<Record<string, string>>({});
    const [availableReasoningEfforts, setAvailableReasoningEfforts] = useState<string[]>([]);
    const [agentReasoningEfforts, setAgentReasoningEfforts] = useState<Record<string, string>>({});
    const [accountResearchLoading, setAccountResearchLoading] = useState(false);
    const [accountResearchEnabled, setAccountResearchEnabled] = useState(false);
    const [accountResearchThreshold, setAccountResearchThreshold] = useState<string>('');
    const [servicesLoading, setServicesLoading] = useState(false);
    const [servicesEnabled, setServicesEnabled] = useState(false);

    const handleDeleteUser = async (userId: string) => {
        try {
            await remove(`/admin/users/${userId}`);
            toast.success('User deleted successfully');
            
            // Refresh the users list in the modal
            if (selectedOrg) {
                const updatedOrg = await get(`/admin/organizations/${selectedOrg.id}`);
                setSelectedOrg(updatedOrg.data);
            }
        } catch (error) {
            console.error('Error deleting user:', error);
            toast.error('Failed to delete user');
        }
    };

    const showUsersModal = async (org: Organization) => {
        try {
            const response = await get(`/admin/organizations/${org.id}`);
            setSelectedOrg(response.data);
            setIsUsersModalOpen(true);
        } catch (error) {
            console.error('Error fetching organization details:', error);
            toast.error('Failed to load organization details');
        }
    };

    const handleEditClick = (org: Organization) => {
        setEditingOrg({ ...org }); // Clone the org to avoid modifying the state directly
        setIsEditModalOpen(true);
        loadRb2bConfig(org.id);
        loadAgentModelConfig(org.id);
        loadAccountResearchConfig(org.id);
        loadServicesConfig(org.id);
    };

    const loadServicesConfig = async (organizationId: string) => {
        setServicesLoading(true);
        try {
            const response = await get(`/admin/organizations/${organizationId}/services`);
            const payload = response.data || {};
            setServicesEnabled(Boolean(payload.enabled));
        } catch (error) {
            setServicesEnabled(false);
            toast.error('Failed to load services settings');
        } finally {
            setServicesLoading(false);
        }
    };

    const loadAccountResearchConfig = async (organizationId: string) => {
        setAccountResearchLoading(true);
        try {
            const response = await get(`/admin/organizations/${organizationId}/account-research`);
            const payload = response.data || {};
            setAccountResearchEnabled(Boolean(payload.enabled));
            setAccountResearchThreshold(
                typeof payload.notify_threshold === 'number'
                    ? String(payload.notify_threshold)
                    : ''
            );
        } catch (error) {
            setAccountResearchEnabled(false);
            setAccountResearchThreshold('');
            toast.error('Failed to load account research settings');
        } finally {
            setAccountResearchLoading(false);
        }
    };

    const loadAgentModelConfig = async (organizationId: string) => {
        setAgentModelConfigLoading(true);
        try {
            const response = await get(`/admin/organizations/${organizationId}/agent-models`);
            const payload = response.data || {};
            setAvailableAgentModels(Array.isArray(payload.available_models) ? payload.available_models : []);
            const nextModels: Record<string, string> = {};
            const nextEfforts: Record<string, string> = {};
            for (const { key } of AGENT_MODEL_DEPLOYMENTS) {
                const value = payload[`${key}_model`];
                nextModels[key] = typeof value === 'string' ? value : '';
                const effort = payload[`${key}_reasoning_effort`];
                nextEfforts[key] = typeof effort === 'string' ? effort : '';
            }
            setAgentModels(nextModels);
            setAvailableReasoningEfforts(
                Array.isArray(payload.available_reasoning_efforts) ? payload.available_reasoning_efforts : []
            );
            setAgentReasoningEfforts(nextEfforts);
        } catch (error) {
            setAvailableAgentModels([]);
            setAgentModels({});
            setAvailableReasoningEfforts([]);
            setAgentReasoningEfforts({});
            toast.error('Failed to load agent model settings');
        } finally {
            setAgentModelConfigLoading(false);
        }
    };

    const loadRb2bConfig = async (organizationId: string) => {
        setRb2bConfigLoading(true);
        try {
            const response = await get(`/admin/organizations/${organizationId}/rb2b-oem`);
            const payload = response.data || {};
            setRb2bEnabled(Boolean(payload.enabled));
            setRb2bIdentifyAll(Boolean(payload.identify_all_visitors));
            setRb2bDomains(Array.isArray(payload.domains) ? payload.domains : []);
            setRb2bResolutionLimit(
                typeof payload.resolution_limit === 'number'
                    ? String(payload.resolution_limit)
                    : ''
            );
        } catch (error) {
            setRb2bEnabled(false);
            setRb2bIdentifyAll(false);
            setRb2bDomains([]);
            setRb2bResolutionLimit('');
            toast.error('Failed to load RB2B OEM settings');
        } finally {
            setRb2bConfigLoading(false);
        }
    };

    const handleSaveEdit = async () => {
        if (!editingOrg) return;
        try {
            // Prepare data for update, potentially parsing dates if needed
            const updateData = {
                name: editingOrg.name,
                license_type: editingOrg.license_type,
                trial_end_date: editingOrg.trial_end_date ? new Date(editingOrg.trial_end_date).toISOString() : null,
                // Add other fields like settings if needed
            };

            await put(`/admin/organizations/${editingOrg.id}`, updateData);
            const trimmedLimit = rb2bResolutionLimit.trim();
            let resolutionLimitPayload: number | null = null;
            if (trimmedLimit !== '') {
                const parsed = Number.parseInt(trimmedLimit, 10);
                if (!Number.isFinite(parsed) || parsed < 0) {
                    toast.error('Resolution limit must be a non-negative integer');
                    return;
                }
                resolutionLimitPayload = parsed;
            }

            await put(`/admin/organizations/${editingOrg.id}/rb2b-oem`, {
                enabled: rb2bEnabled,
                identify_all_visitors: rb2bIdentifyAll,
                resolution_limit: resolutionLimitPayload,
            });
            await put(`/admin/organizations/${editingOrg.id}/agent-models`, {
                ...Object.fromEntries(
                    AGENT_MODEL_DEPLOYMENTS.map(({ key }) => [
                        `${key}_model`,
                        (agentModels[key] || '').trim() || null,
                    ])
                ),
                ...Object.fromEntries(
                    AGENT_MODEL_DEPLOYMENTS.map(({ key }) => [
                        `${key}_reasoning_effort`,
                        (agentReasoningEfforts[key] || '').trim() || null,
                    ])
                ),
            });

            const trimmedThreshold = accountResearchThreshold.trim();
            let thresholdPayload: number | undefined;
            if (trimmedThreshold !== '') {
                const parsedThreshold = Number.parseInt(trimmedThreshold, 10);
                if (!Number.isFinite(parsedThreshold) || parsedThreshold < 1 || parsedThreshold > 100) {
                    toast.error('Fit notification threshold must be between 1 and 100');
                    return;
                }
                thresholdPayload = parsedThreshold;
            }
            await put(`/admin/organizations/${editingOrg.id}/account-research`, {
                enabled: accountResearchEnabled,
                ...(thresholdPayload !== undefined ? { notify_threshold: thresholdPayload } : {}),
            });

            await put(`/admin/organizations/${editingOrg.id}/services`, {
                enabled: servicesEnabled,
            });
            toast.success('Organization updated successfully');
            setIsEditModalOpen(false);
            setEditingOrg(null);
            setRb2bDomainInput('');
            // Note: OrganizationsTab will handle its own refresh
        } catch (error) {
            console.error('Error updating organization:', error);
            toast.error('Failed to update organization');
        }
    };

    const handleAddRb2bDomain = async () => {
        if (!editingOrg) return;
        const nextDomain = rb2bDomainInput.trim();
        if (!nextDomain) {
            return;
        }

        setRb2bDomainsUpdating(true);
        try {
            const response = await fetchAndAddRb2bDomain(editingOrg.id, nextDomain);
            const payload = response.data || {};
            setRb2bDomains(Array.isArray(payload.domains) ? payload.domains : []);
            setRb2bDomainInput('');
            toast.success('RB2B domain added');
        } catch (error: any) {
            const detail = error?.detail;
            toast.error(typeof detail === 'string' ? detail : 'Failed to add RB2B domain');
        } finally {
            setRb2bDomainsUpdating(false);
        }
    };

    const fetchAndAddRb2bDomain = async (organizationId: string, domain: string) => {
        return post(`/admin/organizations/${organizationId}/rb2b-oem/domains`, {
            domain,
        });
    };

    const handleRemoveRb2bDomain = async (domain: string) => {
        if (!editingOrg) return;
        setRb2bDomainsUpdating(true);
        try {
            const response = await remove(
                `/admin/organizations/${editingOrg.id}/rb2b-oem/domains/${encodeURIComponent(domain)}`
            );
            const payload = response.data || {};
            setRb2bDomains(Array.isArray(payload.domains) ? payload.domains : []);
            toast.success('RB2B domain removed');
        } catch (error: any) {
            const detail = error?.detail;
            toast.error(typeof detail === 'string' ? detail : 'Failed to remove RB2B domain');
        } finally {
            setRb2bDomainsUpdating(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!editingOrg) return;
        const { name, value } = e.target;
        setEditingOrg({ ...editingOrg, [name]: value });
    };

    const handleDateChange = (date: Date | undefined) => {
        if (!editingOrg) return;
        // Update the state with the Date object or null
        setEditingOrg({ ...editingOrg, trial_end_date: date ? date.toISOString() : null });
    };

    const loadOrganizations = async () => {
        try {
            const response = await get('/admin/organizations/');
            setOrganizations(response.data || []);
        } catch (error) {
            console.error('Error fetching organizations:', error);
            toast.error('Failed to load organizations');
        }
    };

    const handleEditUserClick = async (user: User) => {
        // Load organizations if not already loaded
        if (organizations.length === 0) {
            await loadOrganizations();
        }
        setEditingUser({ ...user });
        setIsUserEditModalOpen(true);
    };

    const handleSaveUserEdit = async () => {
        if (!editingUser) return;
        try {
            const updateData: any = {};
            if (editingUser.organization_id) {
                updateData.organization_id = editingUser.organization_id;
            }
            if (editingUser.role) {
                updateData.role = editingUser.role;
            }

            await put(`/admin/users/${editingUser.id}`, updateData);
            toast.success('User updated successfully');
            setIsUserEditModalOpen(false);
            setEditingUser(null);
            // Trigger refresh in UsersTab
            setRefreshUsers(prev => prev + 1);
        } catch (error) {
            console.error('Error updating user:', error);
            toast.error('Failed to update user');
        }
    };

    const agentModelOptions = (key: string) => {
        const current = agentModels[key];
        return current && !availableAgentModels.includes(current)
            ? [current, ...availableAgentModels]
            : availableAgentModels;
    };

    return (
        <div className="flex h-full min-h-0 w-full flex-col px-6 py-4">
            <div className="flex min-h-0 flex-1 flex-col">
                <h1 className="text-2xl font-bold mb-4">Admin Panel</h1>
                <Tabs
                    value={activeTab}
                    onValueChange={setActiveTab}
                    orientation="vertical"
                    className="flex min-h-0 min-w-0 flex-1 items-stretch gap-6"
                >
                    <TabsList className="flex h-auto w-44 shrink-0 flex-col items-stretch justify-start gap-1 self-start rounded-lg border bg-card p-2">
                        <TabsTrigger className="w-full justify-start" value="organizations">Organizations</TabsTrigger>
                        <TabsTrigger className="w-full justify-start" value="users">Users</TabsTrigger>
                        <TabsTrigger className="w-full justify-start" value="conversations">Conversations</TabsTrigger>
                        <TabsTrigger className="w-full justify-start" value="visitors">Visitors</TabsTrigger>
                        <TabsTrigger className="w-full justify-start" value="leads">Leads</TabsTrigger>
                        <TabsTrigger className="w-full justify-start" value="agent-runs">Agent Runs</TabsTrigger>
                        <TabsTrigger className="w-full justify-start" value="analytics">Analytics</TabsTrigger>
                        <TabsTrigger className="w-full justify-start" value="metering">Metering</TabsTrigger>
                        <TabsTrigger className="w-full justify-start" value="platform-sql">Platform SQL</TabsTrigger>
                    </TabsList>

                    <div className="min-h-0 min-w-0 flex-1 overflow-y-auto pb-4">
                        <OrganizationsTab
                            onShowUsersModal={showUsersModal}
                        />

                        <UsersTab onEditUser={handleEditUserClick} refreshTrigger={refreshUsers} />

                        <ConversationsTab />

                        <VisitorsTab />

                        <LeadsTab />

                        <AgentRunsTab />

                        <AdminAnalyticsTab />

                        <MeteringTab />

                        <PlatformSqlTab />
                    </div>
                </Tabs>

                {/* Users Modal */}
                <Dialog open={isUsersModalOpen} onOpenChange={setIsUsersModalOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Users in {selectedOrg?.name}</DialogTitle>
                        </DialogHeader>
                        
                        <div className="mt-4">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Name</TableHead>
                                        <TableHead>Email</TableHead>
                                        <TableHead className="text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {(selectedOrg?.users || []).map((user) => (
                                        <TableRow key={user.id}>
                                            <TableCell>{user.name}</TableCell>
                                            <TableCell>{user.email}</TableCell>
                                            <TableCell className="text-right">
                                                <RowDelete
                                                    deleteCallback={() => handleDeleteUser(user.id)}
                                                    deleteMessage={`Are you sure you want to delete user ${user.name}?`}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>

                        <DialogFooter>
                            <Button
                                variant="secondary"
                                onClick={() => setIsUsersModalOpen(false)}
                            >
                                Close
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Edit Organization Modal */}
                <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Edit {editingOrg?.name}</DialogTitle>
                            <DialogDescription>
                                Update organization details.
                            </DialogDescription>
                        </DialogHeader>

                        {editingOrg && (
                            <div className="grid gap-4 py-4">
                                <div className="grid grid-cols-4 items-center gap-4">
                                    <Label htmlFor="name" className="text-right">
                                        Name
                                    </Label>
                                    <Input
                                        id="name"
                                        name="name"
                                        value={editingOrg.name}
                                        onChange={handleInputChange}
                                        className="col-span-3"
                                    />
                                </div>
                                <div className="grid grid-cols-4 items-center gap-4">
                                    <Label htmlFor="license_type" className="text-right">
                                        License Type
                                    </Label>
                                    <Input
                                        id="license_type"
                                        name="license_type"
                                        value={editingOrg.license_type}
                                        onChange={handleInputChange}
                                        className="col-span-3"
                                    />
                                </div>
                                <div className="grid grid-cols-4 items-center gap-4">
                                    <Label htmlFor="trial_end_date" className="text-right">
                                        Trial End Date
                                    </Label>
                                    <div className="col-span-3 flex gap-2">
                                        <Popover>
                                            <PopoverTrigger asChild>
                                                <Button
                                                    variant={"outline"}
                                                    className={cn(
                                                        "flex-1 justify-start text-left font-normal",
                                                        !editingOrg.trial_end_date && "text-muted-foreground"
                                                    )}
                                                >
                                                    <CalendarIcon className="mr-2 h-4 w-4" />
                                                    {editingOrg.trial_end_date ? format(new Date(editingOrg.trial_end_date), "PPP") : <span>Pick a date</span>}
                                                </Button>
                                            </PopoverTrigger>
                                            <PopoverContent className="w-auto p-0">
                                                <Calendar
                                                    mode="single"
                                                    selected={editingOrg.trial_end_date ? new Date(editingOrg.trial_end_date) : undefined}
                                                    onSelect={handleDateChange}
                                                    initialFocus
                                                />
                                            </PopoverContent>
                                        </Popover>
                                        {editingOrg.trial_end_date && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleDateChange(undefined)}
                                            >
                                                Clear
                                            </Button>
                                        )}
                                    </div>
                                </div>
                                <div className="grid grid-cols-4 items-start gap-4">
                                    <Label className="text-right pt-2">
                                        Agent Models
                                    </Label>
                                    <div className="col-span-3 space-y-3">
                                        {AGENT_MODEL_DEPLOYMENTS.map(({ key, label }) => (
                                            <div className="space-y-1" key={key}>
                                                <Label htmlFor={`${key}_agent_model`} className="text-xs text-muted-foreground">
                                                    {label}
                                                </Label>
                                                <Select
                                                    value={agentModels[key] || DEFAULT_MODEL_SELECT_VALUE}
                                                    onValueChange={(value) =>
                                                        setAgentModels((prev) => ({ ...prev, [key]: value === DEFAULT_MODEL_SELECT_VALUE ? '' : value }))
                                                    }
                                                    disabled={agentModelConfigLoading}
                                                >
                                                    <SelectTrigger id={`${key}_agent_model`}>
                                                        <SelectValue placeholder="Use default" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value={DEFAULT_MODEL_SELECT_VALUE}>Use default</SelectItem>
                                                        {agentModelOptions(key).map((model) => (
                                                            <SelectItem key={model} value={model}>
                                                                {model}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                                <Select
                                                    value={agentReasoningEfforts[key] || DEFAULT_MODEL_SELECT_VALUE}
                                                    onValueChange={(value) =>
                                                        setAgentReasoningEfforts((prev) => ({ ...prev, [key]: value === DEFAULT_MODEL_SELECT_VALUE ? '' : value }))
                                                    }
                                                    disabled={agentModelConfigLoading}
                                                >
                                                    <SelectTrigger id={`${key}_agent_effort`} aria-label={`${label} reasoning effort`}>
                                                        <SelectValue placeholder="Default reasoning effort" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value={DEFAULT_MODEL_SELECT_VALUE}>Default reasoning effort</SelectItem>
                                                        {availableReasoningEfforts.map((effort) => (
                                                            <SelectItem key={effort} value={effort}>
                                                                {REASONING_EFFORT_LABELS[effort] || effort}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        ))}

                                        <p className="text-xs text-muted-foreground">
                                            Defaults use the license-based model (website, gateway, email) and the standard app model; gateway and email inherit the website override. Reasoning effort defaults are model-aware (Luna runs at extra high, others at low) and are clamped to what the selected model supports. During provider incidents, Aimdoc may temporarily use an equivalent alternate provider.
                                        </p>
                                    </div>
                                </div>
                                <div className="grid grid-cols-4 items-start gap-4">
                                    <Label htmlFor="rb2b_enabled" className="text-right pt-2">
                                        RB2B OEM
                                    </Label>
                                    <div className="col-span-3 space-y-3">
                                        <label className="flex items-center gap-2 text-sm">
                                            <input
                                                id="rb2b_enabled"
                                                type="checkbox"
                                                checked={rb2bEnabled}
                                                onChange={(event) => setRb2bEnabled(event.target.checked)}
                                                disabled={rb2bConfigLoading}
                                            />
                                            <span>Enable for this organization</span>
                                        </label>

                                        <label className="flex items-center gap-2 text-sm">
                                            <input
                                                id="rb2b_identify_all"
                                                type="checkbox"
                                                checked={rb2bIdentifyAll}
                                                onChange={(event) => setRb2bIdentifyAll(event.target.checked)}
                                                disabled={rb2bConfigLoading || !rb2bEnabled}
                                            />
                                            <span>Identify all visitors</span>
                                        </label>
                                        {rb2bIdentifyAll && (
                                            <p className="text-xs text-muted-foreground ml-6">
                                                Injects the identification pixel immediately when a visitor lands on the page, without waiting for a conversation to start.
                                            </p>
                                        )}

                                        <div className="space-y-1">
                                            <Label htmlFor="rb2b_resolution_limit" className="text-xs text-muted-foreground">
                                                Resolution limit (per billing period)
                                            </Label>
                                            <Input
                                                id="rb2b_resolution_limit"
                                                type="number"
                                                min={0}
                                                step={1}
                                                inputMode="numeric"
                                                placeholder="Default: 1000"
                                                value={rb2bResolutionLimit}
                                                onChange={(event) => setRb2bResolutionLimit(event.target.value)}
                                                disabled={rb2bConfigLoading}
                                            />
                                            <p className="text-xs text-muted-foreground">
                                                Leave blank to use the plan default. Only applies to Convert, Activate, and Aimdoc plans.
                                            </p>
                                        </div>

                                        <div className="flex gap-2">
                                            <Input
                                                placeholder="customer.example.com"
                                                value={rb2bDomainInput}
                                                onChange={(event) => setRb2bDomainInput(event.target.value)}
                                                disabled={rb2bConfigLoading || rb2bDomainsUpdating}
                                                onKeyDown={(event) => {
                                                    if (event.key === 'Enter') {
                                                        event.preventDefault();
                                                        handleAddRb2bDomain();
                                                    }
                                                }}
                                            />
                                            <Button
                                                type="button"
                                                variant="outline"
                                                onClick={handleAddRb2bDomain}
                                                disabled={rb2bConfigLoading || rb2bDomainsUpdating}
                                            >
                                                Add
                                            </Button>
                                        </div>

                                        <div className="space-y-1 max-h-36 overflow-y-auto border rounded-md p-2">
                                            {rb2bDomains.length === 0 ? (
                                                <div className="text-xs text-muted-foreground">
                                                    No RB2B domains configured.
                                                </div>
                                            ) : (
                                                rb2bDomains.map((domain) => (
                                                    <div
                                                        key={domain}
                                                        className="flex items-center justify-between gap-2"
                                                    >
                                                        <span className="text-sm font-mono">{domain}</span>
                                                        <Button
                                                            type="button"
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleRemoveRb2bDomain(domain)}
                                                            disabled={rb2bConfigLoading || rb2bDomainsUpdating}
                                                        >
                                                            Remove
                                                        </Button>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="grid grid-cols-4 items-start gap-4">
                                    <Label htmlFor="account_research_enabled" className="text-right pt-2">
                                        Account Research
                                    </Label>
                                    <div className="col-span-3 space-y-3">
                                        <label className="flex items-center gap-2 text-sm">
                                            <input
                                                id="account_research_enabled"
                                                type="checkbox"
                                                checked={accountResearchEnabled}
                                                onChange={(event) => setAccountResearchEnabled(event.target.checked)}
                                                disabled={accountResearchLoading}
                                            />
                                            <span>Enable for this organization</span>
                                        </label>
                                        <p className="text-xs text-muted-foreground">
                                            Runs a background research agent when an identified visitor links to an
                                            account, storing an ICP fit score and reasoning on the account.
                                        </p>

                                        <div className="space-y-1">
                                            <Label htmlFor="account_research_threshold" className="text-xs text-muted-foreground">
                                                Fit notification threshold (1-100)
                                            </Label>
                                            <Input
                                                id="account_research_threshold"
                                                type="number"
                                                min={1}
                                                max={100}
                                                step={1}
                                                inputMode="numeric"
                                                placeholder="Default: 70"
                                                value={accountResearchThreshold}
                                                onChange={(event) => setAccountResearchThreshold(event.target.value)}
                                                disabled={accountResearchLoading || !accountResearchEnabled}
                                            />
                                            <p className="text-xs text-muted-foreground">
                                                Scores at or above this fire the high-fit account notification.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="grid grid-cols-4 items-start gap-4">
                                    <Label htmlFor="services_enabled" className="text-right pt-2">
                                        Services
                                    </Label>
                                    <div className="col-span-3 space-y-3">
                                        <label className="flex items-center gap-2 text-sm">
                                            <input
                                                id="services_enabled"
                                                type="checkbox"
                                                checked={servicesEnabled}
                                                onChange={(event) => setServicesEnabled(event.target.checked)}
                                                disabled={servicesLoading}
                                            />
                                            <span>Enable for this organization</span>
                                        </label>
                                        <p className="text-xs text-muted-foreground">
                                            Customer-defined background agent services. The flag gates execution;
                                            authoring is always available. Monthly token-cost caps by license:
                                            $200 activate/aimdoc, $50 other paid tiers, trial off.
                                        </p>

                                    </div>
                                </div>
                            </div>
                        )}

                        <DialogFooter>
                            <Button variant="secondary" onClick={() => setIsEditModalOpen(false)}>Cancel</Button>
                            <Button onClick={handleSaveEdit}>Save Changes</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Edit User Modal */}
                <Dialog open={isUserEditModalOpen} onOpenChange={setIsUserEditModalOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Edit User: {editingUser?.name}</DialogTitle>
                            <DialogDescription>
                                Update user organization and role.
                            </DialogDescription>
                        </DialogHeader>

                        {editingUser && (
                            <div className="grid gap-4 py-4">
                                <div className="grid grid-cols-4 items-center gap-4">
                                    <Label className="text-right">
                                        Email
                                    </Label>
                                    <div className="col-span-3 text-sm text-muted-foreground">
                                        {editingUser.email}
                                    </div>
                                </div>
                                <div className="grid grid-cols-4 items-center gap-4">
                                    <Label htmlFor="organization" className="text-right">
                                        Organization
                                    </Label>
                                    <Select 
                                        value={editingUser.organization_id || ''} 
                                        onValueChange={(value) => setEditingUser({ ...editingUser, organization_id: value })}
                                    >
                                        <SelectTrigger className="col-span-3">
                                            <SelectValue placeholder="Select organization" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {organizations.map((org) => (
                                                <SelectItem key={org.id} value={org.id}>
                                                    {org.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="grid grid-cols-4 items-center gap-4">
                                    <Label htmlFor="role" className="text-right">
                                        Role
                                    </Label>
                                    <Select 
                                        value={editingUser.role || 'member'} 
                                        onValueChange={(value) => setEditingUser({ ...editingUser, role: value })}
                                    >
                                        <SelectTrigger className="col-span-3">
                                            <SelectValue placeholder="Select role" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="member">Member</SelectItem>
                                            <SelectItem value="admin">Admin</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        )}

                        <DialogFooter>
                            <Button variant="secondary" onClick={() => setIsUserEditModalOpen(false)}>Cancel</Button>
                            <Button onClick={handleSaveUserEdit}>Save Changes</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </div>
    );
};

export default Admin;